import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CAT_BADGE = {
  support: 'bg-blue-100 text-blue-700',
  complaint: 'bg-amber-100 text-amber-700',
};

export default function SupportTicketsPanel({ token, language = 'hi' }) {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, resolved: 0, total: 0 });
  const [statusFilter, setStatusFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [resolvingNo, setResolvingNo] = useState(null);

  const t = (hi, en) => (language === 'hi' ? hi : en);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/tickets?status=${statusFilter}&limit=150`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setTickets(data.tickets || []);
        setCounts({ open: data.open || 0, resolved: data.resolved || 0, total: data.total || 0 });
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, token]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const resolveTicket = async (ticketNo) => {
    setResolvingNo(ticketNo);
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/tickets/${ticketNo}/resolve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: 'Resolved from dashboard' }),
      });
      const data = await res.json();
      if (data.success) {
        fetchTickets();
      } else {
        alert(t('अपडेट नहीं हो सका, फिर कोशिश करें', 'Could not update, please retry'));
      }
    } catch (err) {
      console.error('Error resolving ticket:', err);
      alert(t('अपडेट नहीं हो सका, फिर कोशिश करें', 'Could not update, please retry'));
    } finally {
      setResolvingNo(null);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow overflow-hidden" data-testid="support-tickets-panel">
      <div className="px-5 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-bold text-indigo-700">🎫 {t('WhatsApp Bot Tickets', 'WhatsApp Bot Tickets')}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{t('Bot से आई Support / Complaint requests — track व resolve करें', 'Support / Complaint requests from the bot — track & resolve')}</p>
        </div>
        <button onClick={fetchTickets} data-testid="tickets-refresh"
          className="text-xs px-3 py-1.5 rounded-lg bg-white border border-indigo-200 text-indigo-600 hover:bg-indigo-50">
          ↻ {t('Refresh', 'Refresh')}
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3 p-4">
        <div className="bg-yellow-50 rounded-xl p-4 text-center border border-yellow-200" data-testid="tickets-open-count">
          <p className="text-xs text-gray-500">⏳ {t('खुले', 'Open')}</p>
          <p className="text-2xl font-bold text-yellow-600">{counts.open}</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 text-center border border-green-200">
          <p className="text-xs text-gray-500">✅ {t('हल हुए', 'Resolved')}</p>
          <p className="text-2xl font-bold text-green-600">{counts.resolved}</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 text-center border border-gray-200">
          <p className="text-xs text-gray-500">📋 {t('कुल', 'Total')}</p>
          <p className="text-2xl font-bold text-gray-700">{counts.total}</p>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 px-4 pb-2">
        {[
          { key: 'open', label: t('खुले', 'Open') },
          { key: 'resolved', label: t('हल हुए', 'Resolved') },
          { key: 'all', label: t('सभी', 'All') },
        ].map((f) => (
          <button key={f.key} onClick={() => setStatusFilter(f.key)}
            data-testid={`tickets-filter-${f.key}`}
            className={`text-xs px-3 py-1.5 rounded-full font-medium ${
              statusFilter === f.key ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="p-10 text-center text-gray-400 text-sm">{t('लोड हो रहा है...', 'Loading...')}</div>
      ) : tickets.length === 0 ? (
        <div className="p-10 text-center text-gray-400 text-sm" data-testid="tickets-empty">
          {t('कोई ticket नहीं', 'No tickets')}
        </div>
      ) : (
        <div className="divide-y">
          {tickets.map((tk) => (
            <div key={tk.ticket_no} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`ticket-${tk.ticket_no}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-indigo-700">{tk.ticket_no}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${CAT_BADGE[tk.category] || 'bg-gray-100 text-gray-700'}`}>
                    {tk.category === 'complaint' ? '⚠️ Complaint' : '🛟 Support'}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${tk.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {tk.status === 'resolved' ? t('हल हुआ', 'Resolved') : t('खुला', 'Open')}
                  </span>
                </div>
                <p className="text-sm text-gray-700 mt-1">{tk.name || 'WhatsApp User'} · 📱 {tk.phone}</p>
                {tk.summary && <p className="text-xs text-gray-500 mt-0.5">{tk.summary}</p>}
                <p className="text-[11px] text-gray-400 mt-1">{tk.created_at ? new Date(tk.created_at).toLocaleString('en-IN') : ''}{tk.resolved_by ? ` · ${t('हल किया', 'by')} ${tk.resolved_by}` : ''}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={`https://wa.me/${(tk.phone || '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                  data-testid={`ticket-wa-${tk.ticket_no}`}
                  className="text-xs px-3 py-2 rounded-lg bg-green-500 text-white font-medium hover:bg-green-600">💬 WhatsApp</a>
                {tk.status !== 'resolved' && (
                  <button onClick={() => resolveTicket(tk.ticket_no)} disabled={resolvingNo === tk.ticket_no}
                    data-testid={`ticket-resolve-${tk.ticket_no}`}
                    className="text-xs px-3 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50">
                    {resolvingNo === tk.ticket_no ? '...' : `✓ ${t('हल हो गया', 'Resolved')}`}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
